/**
 * App shell — the sidebar arrangement: order + the Pinned / More split (per site), and the
 * collapse-to-icons choice (per person).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Plan: the app shell plan (2026-10-08) §2 + §5 — resume block in Documents/Admin-data-table-redesign.md §0
 *
 * The arrangement is ONE site setting, NAV_SIDEBAR_LAYOUT, PropertyValue = JSON
 * `{ v: 1, pinned: [ids], more: [ids] }`, written with customer/upsertSettings. Absent or invalid
 * ⇒ the default (DEFAULT_PINNED, everything else in More in registry order) — so no seeder work.
 * Only Admin + Super Admin may change it (canEditLayout); everyone else sees it read-only.
 *
 * Every function returns NEW arrays / objects; nothing here mutates its input.
 */

export const LAYOUT_SETTING = 'NAV_SIDEBAR_LAYOUT';
export const LAYOUT_VERSION = 1;
export const DEFAULT_PINNED = ['today', 'followups', 'schedule', 'tasks', 'sms', 'chat', 'insurance', 'tx'];
export const COLLAPSED_KEY = 'teamio_nav_collapsed';
export const TAB_BAR_SIZE = 4;

function strings(list) {
  return list.filter(x => typeof x === 'string' && x !== '');
}

/**
 * The stored setting → `{ v, pinned, more }`, or null when it is missing, not JSON, the wrong
 * version, or pinned / more are not arrays. Non-string entries are dropped.
 */
export function parseLayout(text) {
  if (typeof text !== 'string' || text.trim() === '') return null;
  let obj;
  try {
    obj = JSON.parse(text);
  } catch (e) {
    return null;
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  if (obj.v !== LAYOUT_VERSION) return null;
  if (!Array.isArray(obj.pinned) || !Array.isArray(obj.more)) return null;
  return { v: LAYOUT_VERSION, pinned: strings(obj.pinned), more: strings(obj.more) };
}

/**
 * The arrangement to draw for the apps this user can see. `visibleIds` is in registry order.
 *   • null stored ⇒ the default: DEFAULT_PINNED (only the visible ones), the rest in More.
 *   • ids the user cannot see (or that no longer exist) are dropped;
 *   • an id in both lists stays pinned; duplicates keep their first place;
 *   • a visible id in neither list is appended to More, in registry order (a new app, or one
 *     the user just got access to).
 */
export function resolveLayout(stored, visibleIds) {
  const visible = Array.isArray(visibleIds) ? visibleIds.filter(x => typeof x === 'string') : [];
  const isVisible = {};
  visible.forEach((id) => { isVisible[id] = true; });
  const pinnedIn = stored && Array.isArray(stored.pinned) ? stored.pinned : DEFAULT_PINNED;
  const moreIn = stored && Array.isArray(stored.more) ? stored.more : [];
  const seen = {};
  const take = (list) => {
    const out = [];
    list.forEach((id) => {
      if (typeof id === 'string' && isVisible[id] && !seen[id]) {
        seen[id] = true;
        out.push(id);
      }
    });
    return out;
  };
  const pinned = take(pinnedIn);
  const more = take(moreIn).concat(take(visible));
  return { pinned, more };
}

/** The setting's PropertyValue. */
export function serializeLayout(layout) {
  const l = layout || {};
  return JSON.stringify({
    v: LAYOUT_VERSION,
    pinned: Array.isArray(l.pinned) ? l.pinned.slice() : [],
    more: Array.isArray(l.more) ? l.more.slice() : [],
  });
}

function copyLayout(layout) {
  const l = layout || {};
  return {
    pinned: Array.isArray(l.pinned) ? l.pinned.slice() : [],
    more: Array.isArray(l.more) ? l.more.slice() : [],
  };
}

/**
 * Move `id` to `toList` ('pinned' | 'more') at `toIndex` (clamped; omitted ⇒ the end), taking it
 * out of wherever it was. An id in neither list, or an unknown list, returns an unchanged copy.
 */
export function moveItem(layout, id, toList, toIndex) {
  const next = copyLayout(layout);
  if (toList !== 'pinned' && toList !== 'more') return next;
  const inPinned = next.pinned.indexOf(id);
  const inMore = next.more.indexOf(id);
  if (inPinned < 0 && inMore < 0) return next;
  if (inPinned > -1) next.pinned.splice(inPinned, 1);
  if (inMore > -1) next.more.splice(inMore, 1);
  const target = next[toList];
  const n = Number(toIndex);
  const at = Number.isFinite(n) ? Math.max(0, Math.min(Math.floor(n), target.length)) : target.length;
  target.splice(at, 0, id);
  return next;
}

/** Pin: out of More, onto the END of Pinned. Already pinned / unknown ⇒ unchanged copy. */
export function pinApp(layout, id) {
  const next = copyLayout(layout);
  if (next.pinned.indexOf(id) > -1 || next.more.indexOf(id) < 0) return next;
  return moveItem(next, id, 'pinned', next.pinned.length);
}

/** Unpin: out of Pinned, onto the START of More. Not pinned ⇒ unchanged copy. */
export function unpinApp(layout, id) {
  const next = copyLayout(layout);
  if (next.pinned.indexOf(id) < 0) return next;
  return moveItem(next, id, 'more', 0);
}

/** The phone tab bar: the first TAB_BAR_SIZE pinned apps. */
export function tabBarIds(layout, size = TAB_BAR_SIZE) {
  const pinned = layout && Array.isArray(layout.pinned) ? layout.pinned : [];
  return pinned.slice(0, size);
}

/** True when the arrangement is what "Reset to default" would give for these visible ids. */
export function isDefaultLayout(layout, visibleIds) {
  const l = copyLayout(layout);
  const d = resolveLayout(null, visibleIds);
  return JSON.stringify(l.pinned) === JSON.stringify(d.pinned) && JSON.stringify(l.more) === JSON.stringify(d.more);
}

/** Only these two roles may rearrange the office's sidebar. Exact strings — nothing else. */
export function canEditLayout(role) {
  return role === 'Admin' || role === 'Super Admin';
}

/** Collapse-to-icons (per person, this device). Unreadable storage ⇒ expanded. */
export function readCollapsed(storage) {
  try {
    if (!storage || typeof storage.getItem !== 'function') return false;
    return storage.getItem(COLLAPSED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

/** Stores '1' when collapsed, clears the key when expanded. Returns false if storage refused. */
export function writeCollapsed(storage, collapsed) {
  try {
    if (!storage) return false;
    if (collapsed === true) storage.setItem(COLLAPSED_KEY, '1');
    else storage.removeItem(COLLAPSED_KEY);
    return true;
  } catch (e) {
    return false;
  }
}
